import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { PlatformGlyph } from '@/components/publishing/PlatformGlyph';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScheduleTimeField } from '@/components/publishing/ScheduleTimeField';
import { bucketPublishesByLocalDay, localDayKey, publishPlacementIso } from '@/lib/publishing/bucketPublishesByLocalDay';
import {
  buildCalendarMonthDays,
  calendarQueryRange,
  formatLocalTime,
  datetimeLocalValueToIso,
  joinDatetimeLocal,
  localYearMonth,
  resolveCalendarItemTitle,
  shiftYearMonth,
  splitDatetimeLocal,
  viewerTimeZone,
} from '@/lib/publishing/calendarMonth';
import { MY_SCHEDULED_PUBLISHES } from '@/queries/scheduledPublishing';
import { cn } from '@/lib/utils';

type ScheduledRow = {
  id: string;
  platform: string;
  status?: string | null;
  title?: string | null;
  caption?: string | null;
  scheduled_at?: string | null;
  createdAt?: string | null;
};

interface Props {
  id: string;
  testId: string;
  value: string;
  min: string;
  onChange: (value: string) => void;
  timeZone?: string;
}

const WEEKDAY_INDEXES = [0, 1, 2, 3, 4, 5, 6] as const;
const MAX_TITLES_PER_DAY = 2;

export const ScheduleDatetimePicker: React.FC<Props> = ({
  id,
  testId,
  value,
  min,
  onChange,
  timeZone: timeZoneProp,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || 'en';
  const timeZone = timeZoneProp ?? viewerTimeZone();
  const selected = splitDatetimeLocal(value);
  const minParts = splitDatetimeLocal(min);
  const initialCursor = selected
    ? { year: Number(selected.dayKey.slice(0, 4)), month: Number(selected.dayKey.slice(5, 7)) }
    : localYearMonth(timeZone, new Date());
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(initialCursor);
  const todayKey = useMemo(
    () => localDayKey(new Date().toISOString(), timeZone),
    [timeZone],
  );

  const range = useMemo(
    () => calendarQueryRange(cursor.year, cursor.month, timeZone),
    [cursor.year, cursor.month, timeZone],
  );
  const days = useMemo(
    () => buildCalendarMonthDays(cursor.year, cursor.month, timeZone),
    [cursor.year, cursor.month, timeZone],
  );
  const { data } = useQuery(MY_SCHEDULED_PUBLISHES, {
    variables: { from: range.from, to: range.to },
    fetchPolicy: 'cache-and-network',
    skip: !open,
  });
  const buckets = useMemo(
    () => bucketPublishesByLocalDay((data?.myScheduledPublishes || []) as ScheduledRow[], timeZone),
    [data?.myScheduledPublishes, timeZone],
  );

  const monthLabel = useMemo(() => {
    const sample = new Date(Date.UTC(cursor.year, cursor.month - 1, 15, 12));
    return new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(sample);
  }, [cursor.year, cursor.month, locale]);

  const weekdayLabels = useMemo(() => {
    const sunday = new Date(Date.UTC(2026, 7, 2, 12));
    return WEEKDAY_INDEXES.map((offset) => (
      new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
        .format(new Date(sunday.getTime() + offset * 86_400_000))
    ));
  }, [locale]);

  const triggerLabel = useMemo(() => {
    const iso = datetimeLocalValueToIso(value, timeZone);
    if (!iso) return t('publishing.schedule.openCalendar');
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone,
    }).format(new Date(iso));
  }, [locale, t, timeZone, value]);

  const applyDay = (dayKey: string) => {
    const time = selected?.time || minParts?.time || '10:00';
    const next = joinDatetimeLocal(dayKey, time);
    if (next) onChange(next);
  };

  const applyTime = (time: string) => {
    const dayKey = selected?.dayKey || minParts?.dayKey;
    if (!dayKey) return;
    const next = joinDatetimeLocal(dayKey, time);
    if (next) onChange(next);
  };

  return (
    <div className="space-y-1.5">
      <input
        id={id}
        data-testid={testId}
        type="datetime-local"
        className="sr-only"
        min={min}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        tabIndex={-1}
        aria-hidden="true"
      />
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            data-testid={`${testId}-trigger`}
            aria-label={t('publishing.schedule.openCalendar')}
            className="h-10 w-full justify-start gap-2 font-normal"
          >
            <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{value ? triggerLabel : t('publishing.schedule.openCalendar')}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[min(100vw-2rem,22rem)] p-3"
          data-testid={`${testId}-calendar`}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              data-testid={`${testId}-prev-month`}
              onClick={() => setCursor((current) => shiftYearMonth(current.year, current.month, -1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <p className="text-sm font-medium capitalize">{monthLabel}</p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              data-testid={`${testId}-next-month`}
              onClick={() => setCursor((current) => shiftYearMonth(current.year, current.month, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <p className="mb-2 text-[11px] text-muted-foreground">
            {t('publishing.schedule.alreadyScheduled')}
          </p>
          <div className="grid grid-cols-7 gap-px text-center text-[10px] text-muted-foreground">
            {weekdayLabels.map((label, index) => (
              <div key={index} className="py-1">{label}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px">
            {days.map((day) => {
              const items = buckets.get(day.key) || [];
              const isSelected = selected?.dayKey === day.key;
              const isToday = day.key === todayKey;
              const isBeforeMin = !!minParts && day.key < minParts.dayKey;
              const extra = Math.max(0, items.length - MAX_TITLES_PER_DAY);
              return (
                <button
                  key={day.key}
                  type="button"
                  disabled={isBeforeMin}
                  data-testid={`${testId}-day-${day.key}`}
                  onClick={() => applyDay(day.key)}
                  className={cn(
                    'flex min-h-[4.25rem] flex-col rounded-sm border border-transparent p-1 text-left',
                    day.inCurrentMonth ? 'bg-card' : 'bg-muted/30 text-muted-foreground',
                    items.length > 0 && !isSelected && 'bg-primary/5',
                    isToday && !isSelected && 'ring-1 ring-inset ring-primary/40',
                    isSelected && 'border-accent bg-accent text-accent-foreground',
                    isBeforeMin && 'cursor-not-allowed opacity-40',
                    !isSelected && !isBeforeMin && 'hover:bg-accent/20',
                  )}
                >
                  <span className="flex items-center justify-between gap-1">
                    <span className="text-[11px] font-medium">{day.day}</span>
                    {items.length > 0 ? (
                      <span
                        className={cn(
                          'h-1.5 w-1.5 shrink-0 rounded-full',
                          isSelected ? 'bg-accent-foreground' : 'bg-primary',
                        )}
                        aria-hidden="true"
                      />
                    ) : null}
                  </span>
                  <span className="mt-0.5 flex min-h-0 flex-1 flex-col gap-0.5 overflow-hidden">
                    {items.slice(0, MAX_TITLES_PER_DAY).map((item) => {
                      const title = resolveCalendarItemTitle(item) || t('publishing.calendar.untitled');
                      const placement = publishPlacementIso(item);
                      return (
                        <span
                          key={item.id}
                          className={cn(
                            'flex items-start gap-0.5 truncate text-[9px] leading-tight',
                            isSelected ? 'text-accent-foreground' : 'text-muted-foreground',
                          )}
                          title={title}
                        >
                          <PlatformGlyph platform={item.platform} className="h-2.5 w-2.5 shrink-0" />
                          <span className="truncate">
                            {placement ? `${formatLocalTime(placement, timeZone, locale)} ` : ''}
                            {title}
                          </span>
                        </span>
                      );
                    })}
                    {extra > 0 ? (
                      <span className={cn(
                        'text-[9px]',
                        isSelected ? 'text-accent-foreground/80' : 'text-muted-foreground',
                      )}>
                        {t('publishing.schedule.moreScheduled', { count: extra })}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 border-t border-border/60 pt-3">
            <ScheduleTimeField
              id={`${id}-time`}
              testId={`${testId}-time`}
              value={selected?.time || minParts?.time || ''}
              min={selected?.dayKey && minParts && selected.dayKey === minParts.dayKey ? minParts.time : undefined}
              onChange={applyTime}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ScheduleDatetimePicker;
